<template>
  <div>
    <notepad-details
      v-if="isDetailMode"
      @close="switchToListMode"
      @edit-note-click="switchToEditMode"
      @note-detail-loaded="$emit('note-detail-loaded', $event)"
      :reference="reference"
      :id="currentNoteRecord.id"
      :showActions="showActions"
    >
      <template #notepad-header>
        <slot
          name="notepad-header"
        />
      </template>
      <template #note-breadcrumb="props">
        <slot
          name="note-breadcrumb"
          v-bind="props"
        >
        </slot>
      </template>
      <template #note-title="props">
        <slot
          name="note-title"
          v-bind="props"
        />
      </template>
      <template #note-subtitle="props">
        <slot
          name="note-subtitle"
          v-bind="props"
        />
      </template>
    </notepad-details>
    <notepad-list
      v-if="isListMode"
      @note-click="switchToDetailMode"
      @edit-note-click="switchToEditMode"
      @note-list-loaded="$emit('note-list-loaded', $event)"
      :reference="reference"
      :customFilterFunction="customFilterFunction"
      :customDataFunction="customDataFunction"
      :customCreationForm="customCreationForm"
      :customFilterForm="customFilterForm"
      :showActions="showActions"
      :add-note-disabled="addNoteDisabled"
    >
      <template #notepad-header>
        <slot
          name="notepad-header"
        />
      </template>
      <template #note-breadcrumb="props">
        <slot
          name="note-breadcrumb"
          v-bind="props"
        >
        </slot>
      </template>
      <template #note-subtitle="props">
        <slot
          name="note-subtitle"
          v-bind="props"
        />
      </template>
      <template #filter-form="props">
        <slot
          name="filter-form"
          v-bind="props"
        />
      </template>
      <template #creation-form="props">
        <slot
          name="creation-form"
          v-bind="props"
        />
      </template>
    </notepad-list>
    <notepad-edit
      v-if="isEditMode"
      @close="switchToListMode"
      :id="currentNoteRecord.id"
      :customDataFunction="customDataFunction"
      :customCreationForm="customCreationForm"
      :reference="reference"
      @note-edit-loaded="$emit('note-edit-loaded', $event)"
    >

      <template #notepad-header>
        <slot
          name="notepad-header"
        />
      </template>
      <template #creation-form="props">
        <slot
          name="creation-form"
          v-bind="props"
        />
      </template>
    </notepad-edit>
  </div>
</template>

<script>
import NotepadList from '#/notes/components/NotepadList'
import NotepadDetails from '#/notes/components/NotepadDetails'
import NotepadEdit from '#/notes/components/NotepadEdit'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import notesActions from '#/notes/store/actionTypes'
import notesMutation from '#/notes/store/mutationTypes'
import { NOTES_MODES } from './definitions'

/**
 * Groups the Notepad views (List, Edit and Details)
 * Routes and renders the current view
 */
export default {
  name: 'NotepadViews',
  components: {
    NotepadList,
    NotepadEdit,
    NotepadDetails
  },
  props: {
    noteId: {
      type: String
    },
    reference: {
      type: Object,
      required: true
    },
    customFilterForm: {
      type: Object,
      required: true
    },
    customCreationForm: {
      type: Object,
      required: true
    },
    customFilterFunction: {
      type: Function,
      required: true
    },
    customDataFunction: {
      type: Function,
      required: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    addNoteDisabled: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    noteId: {
      handler (id) {
        if (!id) return

        this.switchToDetailMode({id})
      },
      immediate: true
    }
  },
  async created () {
    this.fetchAllDomains()
    this.fetchAllNoteTypes({domain: this.currentDomain, active: true})
    this.fetchAllNoteTags({domain: this.currentDomain, active: true})
  },
  computed: {
    ...mapState('notes', [
      'noteTypes',
      'noteTags',
      'currentNoteRecord'
    ]),
    ...mapGetters('notes', [
      'isEditMode',
      'isDetailMode',
      'isListMode'
    ]),
    currentDomain () {
      return this.reference.reference_domain || null
    }
  },
  methods: {
    ...mapActions({
      fetchAllDomains: notesActions.FETCH_ALL_DOMAINS,
      fetchAllNoteTypes: notesActions.FETCH_ALL_NOTE_TYPES,
      fetchAllNoteTags: notesActions.FETCH_ALL_NOTE_TAGS,
      switchToMode: notesActions.SWITCH_TO_MODE
    }),
    ...mapMutations({
      resetCurrentNote: notesMutation.RESET_CURRENT_NOTE,
      setCurrentNote: notesMutation.SET_CURRENT_NOTE
    }),
    switchToListMode () {
      this.resetCurrentNote()
      this.switchToMode({mode: NOTES_MODES.LIST})
    },
    switchToEditMode (note) {
      this.setCurrentNote(note)
      this.switchToMode({mode: NOTES_MODES.EDIT})
    },
    switchToDetailMode (note) {
      this.setCurrentNote(note)
      this.switchToMode({mode: NOTES_MODES.DETAIL})
    }
  }
}
</script>
